// src/pages/admin/AdminResults.jsx — v4c
import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Paper, Stack, Typography, Button, Table, TableHead,
  TableRow, TableCell, TableBody, CircularProgress, Select,
  MenuItem, FormControl, InputLabel, Chip, Avatar, Tooltip,
  IconButton, alpha,
} from "@mui/material";
import DownloadIcon    from "@mui/icons-material/Download";
import RefreshIcon     from "@mui/icons-material/Refresh";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon      from "@mui/icons-material/Cancel";
import DeleteIcon      from "@mui/icons-material/Delete";
import API from "../../api";
import { useToast } from "../../context/ToastContext";
import { C, gradientText, indigoGradient, P, getPct, pctColor } from "../../theme";
import PaginationBar from "../../components/PaginationBar";

export default function AdminResults() {
  const toast = useToast();
  const [items,      setItems]      = useState([]);
  const [exams,      setExams]      = useState([]);
  const [users,      setUsers]      = useState([]);
  const [total,      setTotal]      = useState(0);
  const [pages,      setPages]      = useState(1);
  const [page,       setPage]       = useState(1);
  const [pageSize,   setPageSize]   = useState(20);
  const [examFilter, setExamFilter] = useState("");
  const [loading,    setLoading]    = useState(false);

  useEffect(() => { document.title = "Résultats — Admin ExamLabs"; }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, page_size: pageSize });
      if (examFilter) p.set("exam_id", examFilter);
      const [aRes, eRes, uRes] = await Promise.all([
        API.get(`/attempts/?${p}`),
        API.get("/exams/?page_size=100"),
        API.get("/users/?page_size=100"),
      ]);
      const aData    = aRes.data;
      const rawItems = Array.isArray(aData) ? aData : (aData.items || []);
      setItems(rawItems);
      setTotal(Array.isArray(aData) ? rawItems.length : (aData.total || rawItems.length));
      setPages(Array.isArray(aData) ? 1 : (aData.pages || 1));
      setExams(Array.isArray(eRes.data) ? eRes.data : (eRes.data.items || []));
      setUsers(Array.isArray(uRes.data) ? uRes.data : (uRes.data.items || []));
    } catch { toast.error("Impossible de charger les résultats."); }
    finally { setLoading(false); }
  }, [page, pageSize, examFilter]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [examFilter]);

  const examTitle = id => exams.find(e => e.id === id)?.title || `Examen #${id}`;
  const username  = id => users.find(u => u.id === id)?.username || `User #${id}`;
  const initials  = id => (username(id)[0] || "?").toUpperCase();

  const exportCSV = () => {
    const rows = [
      ["ID", "Examen", "Utilisateur", "Score", "Max", "%", "Date"],
      ...items.map(a => [
        a.id,
        examTitle(a.exam_id),
        username(a.user_id),
        a.score ?? "",
        a.max_score ?? "",
        getPct(a),
        a.submitted_at ? new Date(a.submitted_at).toLocaleString("fr-FR") : "",
      ]),
    ];
    const csv = rows.map(r =>
      r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }));
    a.download = "resultats.csv";
    a.click();
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette tentative ?")) return;
    try { await API.delete(`/attempts/${id}`); toast.success("Tentative supprimée."); load(); }
    catch { toast.error("Suppression impossible."); }
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between"
        alignItems={{ sm: "center" }} sx={{ mb: 4 }} spacing={2}>
        <Box>
          <Typography variant="overline" color="text.secondary">SUIVI</Typography>
          <Typography variant="h4" fontWeight={700}>
            <Box component="span" sx={gradientText}>Résultats</Box>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {total} tentative{total !== 1 ? "s" : ""}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={load} disabled={loading}
            sx={{ border: `1px solid ${C.slate[300]}`, borderRadius: 2 }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCSV}
            disabled={!items.length} sx={{ textTransform: "none" }}>
            Export CSV
          </Button>
        </Stack>
      </Stack>

      {/* Filtre */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: "10px" }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Filtrer par examen</InputLabel>
          <Select value={examFilter} label="Filtrer par examen"
            onChange={e => setExamFilter(e.target.value)}>
            <MenuItem value="">Tous les examens</MenuItem>
            {exams.map(e => <MenuItem key={e.id} value={e.id}>{e.title}</MenuItem>)}
          </Select>
        </FormControl>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: "12px", overflow: "hidden", border: `1px solid ${C.slate[200]}` }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow sx={{ background: C.slate[50] }}>
                  {["Étudiant", "Examen", "Score", "Résultat", "Date", ""].map((h, i) => (
                    <TableCell key={i}
                      sx={{ fontWeight: 700, fontSize: "0.72rem", textTransform: "uppercase",
                        letterSpacing: "0.05em", color: C.slate[500] }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      Aucune tentative.
                    </TableCell>
                  </TableRow>
                ) : items.map(a => {
                  const pct    = getPct(a);
                  const threshold = exams.find(e => e.id === a.exam_id)?.pass_threshold ?? 50;
                  const passed = pct >= threshold;
                  const col    = pctColor(pct);
                  return (
                    <TableRow key={a.id}
                      sx={{ "&:hover": { background: C.slate[50] }, transition: "background .1s" }}>

                      {/* Étudiant */}
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Avatar sx={{ width: 30, height: 30, fontSize: "0.72rem",
                            fontWeight: 800, background: indigoGradient }}>
                            {initials(a.user_id)}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600}>
                            {username(a.user_id)}
                          </Typography>
                        </Stack>
                      </TableCell>

                      {/* Examen */}
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {examTitle(a.exam_id)}
                        </Typography>
                      </TableCell>

                      {/* Score */}
                      <TableCell>
                        <Chip
                          label={a.max_score ? `${a.score}/${a.max_score}` : (a.score ?? "—")}
                          size="small"
                          sx={{ fontWeight: 800, background: alpha(col, .1), color: col }}
                        />
                        <Typography variant="caption"
                          sx={{ display: "block", color: col, fontWeight: 700, mt: .25 }}>
                          {pct}%
                        </Typography>
                      </TableCell>

                      {/* Résultat */}
                      <TableCell>
                        <Chip size="small"
                          icon={passed
                            ? <CheckCircleIcon sx={{ fontSize: "0.8rem !important" }} />
                            : <CancelIcon     sx={{ fontSize: "0.8rem !important" }} />}
                          label={passed ? "Réussi" : "Échoué"}
                          sx={{ fontWeight: 700,
                            background: alpha(col, .1), color: col,
                            "& .MuiChip-icon": { color: col },
                          }}
                        />
                      </TableCell>

                      {/* Date */}
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {a.submitted_at
                            ? new Date(a.submitted_at).toLocaleDateString("fr-FR", {
                                day: "numeric", month: "short",
                                hour: "2-digit", minute: "2-digit",
                              })
                            : "—"}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <Tooltip title="Supprimer">
                          <IconButton size="small" onClick={() => onDelete(a.id)}
                            sx={{ color: C.red[400] }}>
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Box sx={{ px: 2, pb: 1 }}>
              <PaginationBar
                page={page} pages={pages} total={total} pageSize={pageSize}
                onPageChange={setPage}
                onPageSizeChange={p => { setPageSize(p); setPage(1); }}
                pageSizeOptions={[10, 20, 50]} loading={loading}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}